if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButton) return;

        // Prefer your label span if present (matches your buy-buttons.liquid)
        this.submitButtonText =
          this.submitButton.querySelector('.cbh-atc__label') ||
          this.submitButton.querySelector('span') ||
          this.submitButton;

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Ensure there is exactly ONE hidden variant id input, and it is writable
        this.ensureSingleVariantIdInput();

        // Allow ProductInfo to update the hidden id input
        if (this.variantIdInput) this.variantIdInput.disabled = false;

        // Prefer cart drawer (and re-resolve at submit time)
        this.cart = null;
        this.resolveCart();

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        // Internal submit lock + abort handling
        this._cbhSubmitting = false;
        this._cbhAbortController = null;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }

      /* ---------------------------
        Cart target (drawer preferred)
      ---------------------------- */

      resolveCart() {
        // Prefer drawer first (you want drawer behaviour)
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        return this.cart;
      }

      isCartDrawer() {
        return !!(this.cart && this.cart.tagName && this.cart.tagName.toLowerCase() === 'cart-drawer');
      }

      openCartDrawer(triggeredBy = null) {
        if (!this.cart || !this.isCartDrawer()) return;

        try {
          // Ensure a11y + focus return
          if (triggeredBy) this.cart.setActiveElement(triggeredBy);

          // Open the drawer (your cart-drawer has open())
          if (typeof this.cart.open === 'function') {
            this.cart.open(triggeredBy || this.submitButton);
          } else {
            // Fallback: add active class if open() is missing for any reason
            this.cart.classList.add('active');
          }
        } catch (e) {}
      }

      /* ---------------------------
        Reliability helpers
      ---------------------------- */

      ensureSingleVariantIdInput() {
        if (!this.form) return;

        const inputs = Array.from(this.form.querySelectorAll('input[name="id"]'));
        let keeper = inputs[0];

        if (!keeper) {
          keeper = document.createElement('input');
          keeper.type = 'hidden';
          keeper.name = 'id';
          this.form.prepend(keeper);
        }

        // Remove duplicates
        inputs.slice(1).forEach((i) => i.remove());

        // Ensure it’s enabled so scripts can update it
        keeper.disabled = false;

        // Keep a stable reference
        this._cbhVariantIdInput = keeper;
      }

      getVariantId() {
        const input = this.variantIdInput;
        const id = input ? String(input.value || '').trim() : '';
        return id || null;
      }

      canSubmit() {
        if (!this.form || !this.submitButton) return false;
        if (this._cbhSubmitting) return false;

        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.hasAttribute('disabled')) {
          return false;
        }
        return true;
      }

      /* ---------------------------
        Emit state changes
      ---------------------------- */

      emitState(detail = {}) {
        try {
          this.dispatchEvent(
            new CustomEvent('product-form:state', {
              bubbles: true,
              detail: {
                sectionId: this.closest('product-info')?.dataset.section || null,
                formId: this.form?.id || null,
                inSticky: !!this.closest('.sticky-atc'),
                variantId: this.variantIdInput?.value || null,
                ...detail,
              },
            })
          );
        } catch (e) {}
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        const spinner = this.querySelector('.loading__spinner');

        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.setAttribute('aria-busy', 'true');
          this.form?.setAttribute('aria-busy', 'true');
          this.submitButton.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        } else {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-busy');
          this.form?.removeAttribute('aria-busy');
          if (spinner) spinner.classList.add('hidden');

          // Only clear aria-disabled if not disabled for real stock reasons.
          if (!this.submitButton.hasAttribute('disabled')) {
            this.submitButton.removeAttribute('aria-disabled');
          }
        }

        this.emitState({
          loading: !!isLoading,
          ariaDisabled: this.submitButton.getAttribute('aria-disabled') === 'true',
          disabled: this.submitButton.hasAttribute('disabled'),
          text: (this.submitButtonText?.textContent || '').trim(),
        });
      }

      /* ---------------------------
        Error normaliser (cart add)
      ---------------------------- */

      normaliseCartAddError(err) {
        try {
          if (!err) return '';
          if (typeof err === 'string') return err;
          if (Array.isArray(err)) return err.filter(Boolean).join(' ');
          if (typeof err === 'object') {
            const parts = [];
            for (const k in err) {
              const v = err[k];
              if (Array.isArray(v)) parts.push(...v);
              else if (typeof v === 'string') parts.push(v);
            }
            return parts.filter(Boolean).join(' ');
          }
          return '';
        } catch (e) {
          return '';
        }
      }


      getPurchaseQuantityContext() {
        const input = this.closest('product-info')?.querySelector('input[name="quantity"]');
        if (!input) return null;

        const requested = Number.parseInt(input.value, 10);
        const min = input.min !== '' ? Number.parseInt(input.min, 10) : null;
        const max = input.max !== '' ? Number.parseInt(input.max, 10) : null;
        const step = Number.parseInt(input.step || '1', 10) || 1;
        const cartQuantity = Number.parseInt(input.dataset.cartQuantity || '0', 10) || 0;
        const limitSource = input.dataset.limitSource || '';

        return {
          input,
          requested: Number.isFinite(requested) ? requested : null,
          min: Number.isFinite(min) ? min : null,
          max: Number.isFinite(max) ? max : null,
          step,
          cartQuantity,
          limitSource,
        };
      }

      validatePurchaseQuantity() {
        const qty = this.getPurchaseQuantityContext();
        if (!qty) return null;

        if (qty.max !== null && qty.max <= 0) {
          if (qty.limitSource === 'rule') return 'The maximum quantity for this option is already in your basket.';
          if (qty.limitSource === 'both') return 'This option has reached its current basket limit.';
          return 'The available stock is already in your basket.';
        }

        if (qty.max !== null && qty.requested !== null && qty.requested > qty.max) {
          const remainingText = qty.cartQuantity > 0 ? `${qty.max} more` : `${qty.max}`;
          return `Only ${remainingText} can be added right now. Please reduce the quantity and try again.`;
        }

        if (qty.requested === null || qty.requested <= 0) {
          return 'Please choose a valid quantity.';
        }

        if (qty.min !== null && qty.requested < qty.min) {
          return `The minimum quantity you can add right now is ${qty.min}.`;
        }

        if (qty.min !== null && qty.step > 1 && (qty.requested - qty.min) % qty.step !== 0) {
          return `Please choose a quantity in steps of ${qty.step}.`;
        }

        return null;
      }

      getCartAddError(response = {}) {
        const raw = [
          response.description,
          response.message,
          this.normaliseCartAddError(response.errors),
        ]
          .filter(Boolean)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();
        const lower = raw.toLowerCase();

        const unavailable =
          lower.includes('sold out') ||
          lower.includes('out of stock') ||
          lower.includes('no longer available') ||
          lower.includes('is not available');

        if (unavailable) {
          return {
            unavailable: true,
            message: 'This item is no longer available. Please refresh the page or choose another option.',
          };
        }

        if (
          lower.includes("can't add more") ||
          lower.includes('cannot add more') ||
          lower.includes('maximum') ||
          lower.includes('quantity') ||
          lower.includes('inventory') ||
          lower.includes('stock')
        ) {
          const qty = this.getPurchaseQuantityContext();

          if (qty?.max !== null && qty?.requested !== null && qty.requested > qty.max) {
            const remainingText = qty.cartQuantity > 0 ? `${qty.max} more` : `${qty.max}`;
            return {
              unavailable: false,
              message: `Only ${remainingText} can be added right now. Please reduce the quantity and try again.`,
            };
          }

          return {
            unavailable: false,
            message: 'There isn’t enough stock for that quantity. Please reduce the quantity and try again.',
          };
        }

        if (lower.includes('selling plan') || lower.includes('subscription')) {
          return {
            unavailable: false,
            message: 'Please open the product and choose the required purchase option before adding it to your basket.',
          };
        }

        if (lower.includes('variant') || lower.includes('option')) {
          return {
            unavailable: false,
            message: 'That option is no longer available. Please refresh the page and choose again.',
          };
        }

        return {
          unavailable: false,
          message: 'We couldn’t add this item to your basket. Please try again.',
        };
      }

      markSubmitSoldOut() {
        const soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
        if (!this.submitButton || !soldOutMessage) return;

        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.setAttribute('aria-disabled', 'true');
        if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
      }

      announceCartSuccess() {
        const liveRegion =
          document.getElementById('CartDrawer-LiveRegionText') ||
          document.getElementById('cart-live-region-text');
        if (!liveRegion) return;

        const contextualTitle =
          this.dataset.productTitle ||
          this.closest('[data-cbh-product-title]')?.dataset.cbhProductTitle ||
          '';
        const message = contextualTitle
          ? `${contextualTitle} added to your basket.`
          : 'Added to your basket.';

        liveRegion.textContent = '';
        requestAnimationFrame(() => {
          liveRegion.textContent = message;
        });
      }

      /* ---------------------------
        Submit
      ---------------------------- */

      onSubmitHandler(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        evt.stopImmediatePropagation();

        if (!this.canSubmit()) return;

        const submitTrigger =
          evt.submitter instanceof HTMLElement
            ? evt.submitter
            : document.activeElement instanceof HTMLElement
              ? document.activeElement
              : this.submitButton;

        // Re-resolve cart at submit time (prevents “drawer not in DOM yet” issues)
        this.resolveCart();

        // Make sure we still have one id input (sections can re-render)
        this.ensureSingleVariantIdInput();

        this.handleErrorMessage();

        const quantityError = this.validatePurchaseQuantity();
        if (quantityError) {
          this.handleErrorMessage(quantityError);
          this.getPurchaseQuantityContext()?.input?.focus?.();
          this.emitState({ submitted: true, success: false, error: true, message: quantityError });
          return;
        }

        const variantId = this.getVariantId();
        if (!variantId) {
          this.handleErrorMessage(window.variantStrings?.unavailable || 'Please select an option.');
          this.emitState({ submitted: true, success: false, error: true, message: 'Missing variant id' });
          return;
        }

        this.error = false;
        this._cbhSubmitting = true;
        this.setLoading(true);

        // Abort any previous pending request for this component
        try {
          this._cbhAbortController?.abort?.();
        } catch (e) {}
        this._cbhAbortController = window.AbortController ? new AbortController() : null;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        formData.set('id', variantId);

        if (this.cart) {
          // cart-drawer + cart-notification both use .getSectionsToRender().map(s => s.id)
          try {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(submitTrigger || document.activeElement);
          } catch (e) {}
        }

        config.body = formData;
        if (this._cbhAbortController) config.signal = this._cbhAbortController.signal;

        const cartAddUrl =
  (window.routes && window.routes.cart_add_url) ||
  (typeof routes !== 'undefined' && routes && routes.cart_add_url) ||
  '/cart/add';

fetch(cartAddUrl, config)
          .then((response) => response.json())
          .then((response) => {
            // Shopify “error” shape
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              const cartError = this.getCartAddError(response);
              this.handleErrorMessage(cartError.message);

              // Only convert the CTA to a permanent Sold out state when Shopify
              // explicitly reports an availability failure. Quantity or API errors
              // must remain retryable.
              if (cartError.unavailable) this.markSubmitSoldOut();

              this.error = true;
              this.emitState({
                submitted: true,
                success: false,
                error: true,
                message: cartError.message,
                unavailable: cartError.unavailable,
              });
              return;
            }

            // If no cart UI exists, fall back to cart page
            if (!this.cart) {
              const cartUrl =
  (window.routes && window.routes.cart_url) ||
  (typeof routes !== 'undefined' && routes && routes.cart_url) ||
  '/cart';

window.location = cartUrl;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            const cartFocusReturnTarget = quickAddModal?.openedBy || submitTrigger || this.submitButton;

            const renderAndOpen = () => {
              try {
                this.cart.renderContents(response);
              } catch (e) {}

              this.announceCartSuccess();

              // After Quick Add closes, the cart drawer must return focus to the
              // card CTA that opened the modal, not to a submit button that has
              // just been removed with the modal content.
              this.openCartDrawer(cartFocusReturnTarget);
            };

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    renderAndOpen();
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              renderAndOpen();
            }

            this.emitState({ submitted: true, success: true, error: false });
          })
          .catch((e) => {
            if (e && e.name === 'AbortError') return;

            window.cbhLogError?.(e);
            this.error = true;

            this.emitState({
              submitted: true,
              success: false,
              error: true,
              message: e && e.message ? e.message : 'Request failed',
            });

            if (!this.hideErrors) {
              this.handleErrorMessage('We couldn’t add this item to your basket. Please check your connection and try again.');
            }
          })
          .finally(() => {
            this._cbhSubmitting = false;

            this.setLoading(false);

            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            // If not error, re-enable aria-disabled (but NOT disabled attribute)
            if (!this.error && this.submitButton) {
              if (!this.submitButton.hasAttribute('disabled')) {
                this.submitButton.removeAttribute('aria-disabled');
              }
            }
          });
      }

      /* ---------------------------
        Errors + toggle
      ---------------------------- */

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        if (!this.errorMessage) return;

        // Show/hide
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        // Standardised styling hook
        if (errorMessage) {
          this.errorMessageWrapper.classList.add('cart-error');
          this.errorMessage.textContent = errorMessage;
        } else {
          this.errorMessageWrapper.classList.remove('cart-error');
          this.errorMessage.textContent = '';
        }
      }

      toggleSubmitButton(disable = true, text) {
  if (!this.submitButton) return;

  const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
  const defaultText =
    this.submitButton.dataset.defaultLabel ||
    (window.variantStrings && window.variantStrings.addToCart) ||
    'Add to basket';

  if (disable) {
    this.submitButton.setAttribute('disabled', 'disabled');
    this.submitButton.setAttribute('aria-disabled', 'true');
    if (text && this.submitButtonText) {
      this.submitButtonText.textContent = text;
      this.submitButtonText.classList.remove('hidden');
      if (soldOutMessage) soldOutMessage.classList.add('hidden');
    }
  } else {
    this.submitButton.removeAttribute('disabled');
    this.submitButton.removeAttribute('aria-disabled');

    // ✅ Restore label visibility if it was hidden by a prior sold-out/error state
    if (this.submitButtonText) this.submitButtonText.classList.remove('hidden');
    if (soldOutMessage) soldOutMessage.classList.add('hidden');

    if (this.submitButtonText) this.submitButtonText.textContent = defaultText;
  }

  this.emitState({
    toggled: true,
    disabled: this.submitButton.hasAttribute('disabled'),
    ariaDisabled: this.submitButton.getAttribute('aria-disabled') === 'true',
    text: (this.submitButtonText?.textContent || '').trim(),
  });
}

      /* ---------------------------
        Getters
      ---------------------------- */

      get variantIdInput() {
        if (this._cbhVariantIdInput) return this._cbhVariantIdInput;
        return this.form?.querySelector('[name=id]');
      }
    }
  );
}
